import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import type { AgentTrackOutcome, IAgentLocationDto } from '../interface/child-agent';

// Standalone client for the Core API's agent-location read endpoint. Same
// isolation contract as the other coverage clients (JWT + PartyId, no
// 401->logout). NOTE: this endpoint is administrator-only today and returns 403
// for party-agent tokens — the caller (MapTab) maps 403 to a calm "not yet
// enabled" state. The PartyId + EntityCode filters already exist server-side, so
// once the authorization gate is relaxed to party scope (mirroring
// AgentAssignment/by-entity) this call returns the child's live location with no
// frontend change.
const agentTrackApi = axios.create({ baseURL: COREAPI });

agentTrackApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

agentTrackApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

const STALE_MINUTES = 15;

/**
 * Fetch the latest known location(s) for agents on a given entity code, scoped
 * to the caller's party. Returns the enveloped `{ data: AgentLocationDto[] }`.
 * Non-2xx resolves (validateStatus) so the caller can distinguish 403 (admin
 * gate) from an empty result or a genuine error.
 */
export const getLatestAgentLocation = async (entityCode: string): Promise<AgentTrackOutcome> => {
	const { user } = getUserData();
	const params: Record<string, string | number> = {
		EntityCode: entityCode,
		StaleMinutes: STALE_MINUTES,
	};
	if (user?.PartyId) params.PartyId = user.PartyId;

	try {
		const res = await agentTrackApi.get<{ data?: IAgentLocationDto[] }>('/api/v1/Track/latest', {
			params,
			validateStatus: () => true,
		});
		return { reachable: true, httpStatus: res.status, body: res.data ?? null };
	} catch {
		return { reachable: false };
	}
};
